import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

// Reusable legal document overlay (Institutional Terms, Privacy Policy).
// Opens over the current page and closes back to it, so the reader never loses their place.
export const PolicyModal = ({ open, onClose, tKey, testId }) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const sections = t(`${tKey}.sections`, { returnObjects: true }) || [];

  return (
    <div
      data-testid={`${testId}-modal`}
      className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto animate-fade-in"
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        data-testid={`${testId}-modal-backdrop`}
      />
      <div className="relative bg-primary-custom border border-subtle-custom shadow-2xl w-full max-w-3xl my-12 md:my-20 mx-4 md:mx-6">
        <button
          onClick={onClose}
          data-testid={`${testId}-modal-close`}
          aria-label="Close"
          className="absolute top-5 right-5 md:top-6 md:right-6 text-secondary-custom hover:text-primary-custom transition-colors duration-300 z-10"
        >
          <X size={22} strokeWidth={1.4} />
        </button>

        <div className="px-6 md:px-14 py-12 md:py-16">
          <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block mb-5">
            {t(`${tKey}.eyebrow`)}
          </span>
          <h2 className="font-serif text-3xl md:text-4xl font-light text-primary-custom mb-4 leading-tight">
            {t(`${tKey}.title`)}
          </h2>
          <p className="text-[11px] text-secondary-custom uppercase tracking-[0.18em] mb-10">
            {t(`${tKey}.updated`)}
          </p>

          <div className="space-y-9">
            {sections.map((s, i) => (
              <div key={i} data-testid={`${testId}-section-${i}`}>
                <h3 className="font-serif text-lg md:text-xl font-normal text-primary-custom mb-2">
                  {s.heading}
                </h3>
                <p className="font-serif text-sm md:text-base text-secondary-custom leading-relaxed font-light">
                  {s.body}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-subtle-custom">
            <button
              onClick={onClose}
              data-testid={`${testId}-modal-return`}
              className="border border-[var(--accent-bronze)] text-[var(--accent-bronze)] px-8 py-3 text-[10px] font-sans tracking-[0.25em] uppercase hover:bg-[var(--accent-bronze)] hover:text-white transition-all duration-300"
            >
              {t(`${tKey}.close`)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyModal;
